import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { GraduationCap, BookOpen, ChartLineUp, Sparkle, ArrowRight } from "@phosphor-icons/react";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-bone text-ink">
      <header className="flex items-center justify-between px-8 md:px-16 py-6 border-b border-stone">
        <div className="flex items-center gap-3">
          <GraduationCap size={28} weight="duotone" className="text-forest" />
          <div>
            <div className="font-serif text-2xl leading-none text-forest">ControlEra</div>
            <div className="overline">Academy LMS</div>
          </div>
        </div>
        <div className="flex gap-3">
          {user ? (
            <button onClick={() => navigate("/")} className="btn-primary" data-testid="landing-go-dash">Go to dashboard</button>
          ) : (
            <>
              <button onClick={() => navigate("/login")} className="btn-ghost" data-testid="landing-login">Sign in</button>
            </>
          )}
        </div>
      </header>

      <section className="relative px-8 md:px-16 py-20 md:py-28 overflow-hidden">
        <div className="max-w-5xl">
          <div className="overline mb-6">est. a centralised academic platform · 2026</div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-[88px] leading-[0.95] tracking-tight text-forest">
            A quieter way to run <em className="text-terracotta not-italic">examinations</em>, assignments, and academic performance.
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-mossy leading-relaxed">
            ControlEra consolidates question banks, AI-assisted paper generation, online MCQ assessments, assignment workflows, lecture archives, and analytical dashboards into a single rigorous platform for Classes 9–12, B.Com, B.Sc., and B.CA.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => navigate(user ? "/" : "/login")} className="btn-primary flex items-center gap-2" data-testid="cta-start">
              Begin a session <ArrowRight size={16} />
            </button>
            <button onClick={() => navigate("/login")} className="btn-secondary" data-testid="cta-signin">I already have an account</button>
          </div>
        </div>

        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-6">
          <FeatureCard icon={BookOpen} title="Question Repositories" body="MCQs and descriptive questions, organised subject-wise and chapter-wise for Classes 9–12 and undergraduate streams." />
          <FeatureCard icon={Sparkle} title="AI paper generation" body="Draft chapter-wise or mixed-chapter papers with Claude Sonnet 4.5. Review, edit, and finalise before publishing." />
          <FeatureCard icon={ChartLineUp} title="Performance analysis" body="Strong/weak chapters, month-wise consolidations, annual reports, and class-average comparisons." />
        </div>
      </section>

      <section className="px-8 md:px-16 py-16 border-t border-stone bg-ivory">
        <div className="overline mb-3">For the institution</div>
        <h2 className="font-serif text-3xl md:text-5xl text-forest max-w-3xl">Built for faculty who value rigour, and students who value clarity.</h2>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            ["Teachers", "Curate question banks, publish MCQ tests, grade descriptive submissions, upload lectures, and send SMS/email alerts."],
            ["Students", "Take timed assessments, track strong and weak chapters, download study material, and view annual report cards."],
            ["Admins", "Manage users, configure subjects/chapters, and audit platform-wide analytics."],
            ["Notifications", "Push, Email (SendGrid) and SMS (Twilio) channels keep everyone informed of exams, results, and deadlines."],
          ].map(([t, b]) => (
            <div key={t} className="border-l-2 border-forest pl-5">
              <h3 className="font-serif text-2xl text-forest mb-2">{t}</h3>
              <p className="text-sm text-mossy leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="px-8 md:px-16 py-8 border-t border-stone text-xs text-mossy flex justify-between">
        <span>© 2026 ControlEra Academy LMS</span>
        <span className="font-mono">v1.0 · ars docendi</span>
      </footer>
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body }) {
  return (
    <div className="card-academic p-6">
      <Icon size={28} weight="duotone" className="text-terracotta mb-4" />
      <h3 className="font-serif text-2xl text-forest mb-2">{title}</h3>
      <p className="text-sm text-mossy leading-relaxed">{body}</p>
    </div>
  );
}
